/**
 * Custom domain types (tasks 844 / 850).
 *
 * A custom domain is a customer-owned hostname fronting the Account's whole
 * endpoint surface family — invocation, MCP and the generated interface — so
 * `https://ai.acme.com/compose-endpoint-tests` serves what
 * `https://innovate-api.monarchworks.ai/api/acme/compose-endpoint-tests` serves.
 *
 * AMS is the registry of record; innovate-service proxies its CRUD at
 * `/api/account/custom-domains` and adds the entitlement projection the settings
 * section renders its plan gate from. Every field below is AMS's — including
 * `target`, the CNAME destination, which is echoed on every record precisely so
 * no surface hardcodes it.
 */

/**
 * `pending_dns` → the customer has not pointed their CNAME yet.
 * `verifying`   → the CNAME resolves; the managed certificate is being issued.
 * `active`      → serving.
 * `failed`      → terminal; `failureReason` says why.
 */
export type CustomDomainStatus = 'pending_dns' | 'verifying' | 'active' | 'failed';

export interface CustomDomain {
  id: string;
  accountId: string;
  hostname: string;
  status: CustomDomainStatus;
  /** The stable CNAME target to point the hostname at, e.g. `endpoints.monarchworks.ai`. */
  target: string;
  verifiedAt: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Why the account may not add another domain. Each maps to its own copy. */
export type CustomDomainGateReason =
  | 'no_active_plan'
  | 'not_entitled'
  | 'limit_unresolved'
  | 'limit_reached'
  | 'entitlements_unresolved';

/**
 * The account's live plan answer for this feature, resolved server-side from
 * AMS — never derived in the browser and never hardcoded. Fails CLOSED: when
 * the plan cannot be read, `canAdd` is false with the reason spelled out.
 */
export interface CustomDomainEntitlement {
  /** The plan holds the `custom_domain` feature. */
  entitled: boolean;
  /** The add-domain form should be enabled. */
  canAdd: boolean;
  /** The plan's `max_custom_domains` cap; `-1` means unlimited, `null` unresolved. */
  limit: number | null;
  unlimited: boolean;
  /** Domains currently registered. */
  used: number;
  /** Always set when `canAdd` is false. */
  reason: CustomDomainGateReason | null;
  planSlug: string | null;
}

/** The whole section in one read — domains and the plan gate, always consistent. */
export interface CustomDomainsView {
  domains: CustomDomain[];
  entitlement: CustomDomainEntitlement;
}

/** Statuses that are still settling, so the list keeps polling. */
export const SETTLING_CUSTOM_DOMAIN_STATUSES: readonly CustomDomainStatus[] = [
  'pending_dns',
  'verifying',
];

/** True while this domain has not reached `active` or `failed`. */
export const isCustomDomainSettling = (domain: CustomDomain): boolean =>
  SETTLING_CUSTOM_DOMAIN_STATUSES.includes(domain.status);
